// React Deprecating default props, temporary eslint disable rule
/* eslint react/require-default-props: 0 */

import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { useSelector } from 'react-redux';
import { setHelmetTags } from 'helpers/HelmetHelper';
import ContextPlayer from 'components/ContextPlayer';
import Breadcrumbs from 'components/Breadcrumbs';
import { Hero, ANON_SERIES_V2 } from 'componentsV2/Hero';
import { noop } from 'helpers/GlobalHelper';
import {
  isTypeSeason,
  activePlansHasFreeTrial,
  PANEL_RECOMMENDED_SERIES,
} from 'helpers/ContentHelper';
import { getRecommendationId, setSegmentContentPersonality } from '@foxcorp/delta-web-analytics';
import { redirectToExternalUrl } from 'helpers/RouteHelper';
import Schema from 'componentsV2/Schema';
import { SCHEMA_TYPES } from 'componentsV2/Schema/SchemaHelper';
import { hasPrePremiereVideo, getPromoData, PROMO_TYPE_LEAD } from 'helpers/SeriesDetailHelpers';
// TODO: Move imported assets below to local directory once all platforms use new design
import NationWhatIs from 'screens/ContentDetail/components/NationWhatIs';
import StartFreeTrial from 'screens/ContentDetail/components/StartFreeTrial';
import FloatingStartFreeTrial from 'components/FloatingStartFreeTrial';
import Loader from 'components/Loader';
import { getVariant, exposureEvent, EXPERIMENTS, VARIANTS } from 'helpers/AmplitudeHelper';
import CarouselSection from './CarouselSection';
import About from './About';
import Recommended from './Recommended';
import { overrideMetatags } from '../ContentDetailHelper';
import TabListSeasons from './Tablist/Tablist';
import SeriesDetailStyle from '../styles/SeriesDetail.module.scss';

const AnonymousView = ({
  accessToken = null,
  anonymousAccount = null,
  bookmarks,
  breadcrumbsData = null,
  breakpoint,
  contextPlayerVisibility = false,
  disableScroll,
  recommended,
  deviceId,
  favoritableItems = [],
  favoritesManager = {},
  inline = {},
  isAuthenticated,
  isAnonymous,
  isPersonality = false,
  onCloseModal = noop,
  overlay,
  pageViewEvent,
  params: { showId, pageType, pageId },
  profileId,
  preauthorizedResources,
  recommendationIdData = null,
  promos = {},
  seo = {},
  seriesData = {},
  seriesType = '',
  setOverlay,
  tabIndex,
  tabs: sections = [],
}) => {
  const { activePlans } = useSelector(state => state.app);
  const isFreeTrialPlanActive = activePlansHasFreeTrial(activePlans);
  const item = _.get(inline, 'items.member.0', null);
  const recommendationId = getRecommendationId(recommendationIdData);
  const panelProps = {
    breakpoint,
    collectionCount: sections.length,
    inline,
    seriesType,
  };
  const aboutProps = {
    breakpoint,
    description: seriesData?.longDescription ?? '',
    image: seriesData?.images?.seriesList?.raw ?? null,
    name: seriesData?.name ?? '',
  };
  const isSeasonAsc = seriesData?.orderSeasons === 'asc';
  const tabs = sections?.filter(section => {
    const sectionType = section?.['@type'] ?? null;
    return isTypeSeason(sectionType);
  });

  const contentPersonality = _.get(seriesData, 'categoryTags');

  if (contentPersonality) setSegmentContentPersonality(contentPersonality);
  const recommendedProps = {
    accessToken,
    breakpoint,
    collectionCount: sections.length,
    deviceId,
    overrideAdditionalPosterStyle: true,
    pageType,
    recommended,
    seriesType,
    verticalPosition: 4,
  };

  const video = _.get(seriesData, 'previewVideo.member.0', null);
  if (video) video.isTrailer = true;
  const title = _.get(video, 'name', '');
  const hasPromos = !_.isEmpty(promos);
  const promoLeadData = getPromoData({ data: promos, promoType: PROMO_TYPE_LEAD });
  const promoLeadContent = _.get(promoLeadData, 'items.member[0]', []);
  const promoImage = _.get(seriesData, 'images.seriesList.raw');
  const promoLink = _.get(promoLeadContent, 'url', '');
  const promoDescription = _.get(promoLeadContent, 'description', '');
  const promoCTA = _.get(promoLeadContent, 'ctaText', '');
  const promoProps = {
    promoImage,
    promoLogo: '/static/images/nation-logo-230x70.png',
    promoDescription,
    promoCTA,
  };
  const onClickCTA = () => promoLink && redirectToExternalUrl(promoLink);
  const hasLoadedContent = seriesData;
  if (!hasLoadedContent) return <Loader />;
  const trailer =
    _.get(seriesData, 'previewVideo.member.0') ||
    _.get(inline, '0.items.member.0.previewVideo.@populated');
  const hasTrailer = _.get(trailer, '@populated', false) || hasPrePremiereVideo(inline);
  let schemaType;
  let schemaItem = seriesData;

  switch (seriesType) {
    case 'movie':
      schemaType = SCHEMA_TYPES.MOVIES;
      schemaItem = seriesData?.latestMovie;
      break;
    case 'special':
      schemaType = SCHEMA_TYPES.MOVIES;
      schemaItem = seriesData?.latestEpisode;
      break;
    case 'show':
    case 'series':
      schemaType = SCHEMA_TYPES.SERIES;
      break;
    default:
      break;
  }

  if (isPersonality) schemaType = SCHEMA_TYPES.PERSON;
  const schema = [{ item: schemaItem, type: schemaType }];

  if (hasTrailer) schema.push({ item: trailer, type: SCHEMA_TYPES.VIDEO_OBJECT });

  const upgradeCTAVariant = getVariant({ key: EXPERIMENTS.WEB_ANON_CTA });
  const anonymousV2 = upgradeCTAVariant === VARIANTS.VARIANT;

  if (upgradeCTAVariant) {
    exposureEvent({ experimentKey: EXPERIMENTS.WEB_ANON_CTA });
  }

  const tablistProps = {
    accessToken,
    collectionCount: sections.length,
    bookmarks,
    tabs: isSeasonAsc ? tabs?.reverse() : tabs,
    tabIndex,
    inline,
    setOverlay,
    breakpoint,
    overlay,
    seriesData,
    isSeriesDetailPage: true,
    showId,
    pageId,
    pageType,
    pageViewEvent,
    isAuthenticated,
    preauthorizedResources,
    showUpgradeCTA: anonymousV2,
    utagSeriesType: seriesType,
    verticalPosition: 2,
  };
  return (
    <>
      {setHelmetTags(seo)}
      <Schema schemas={schema} />
      {overrideMetatags({ seriesData, isPersonality })}
      <Hero
        accessToken={accessToken}
        bookmarks={bookmarks}
        disableScroll={disableScroll}
        favoriteProps={{ favoritesManager, favoritableItems, recommendationId }}
        item={item}
        panel={seriesData}
        profileId={profileId}
        type={ANON_SERIES_V2}
        showUpgradeCTA={anonymousV2}
      />
      {sections && <TabListSeasons {...tablistProps} />}
      {sections &&
        sections.map((panel, index) => {
          if (!panel) return null;
          const displayTemplate = _.get(panel, 'displayTemplate');
          const isClips = _.get(panel, 'videoTypes', []).includes('clip');
          const isRecommended = panel?.headline?.toLowerCase() === PANEL_RECOMMENDED_SERIES;
          const isPosters = displayTemplate === 'featured' || isRecommended;
          if (isClips || isRecommended) {
            return CarouselSection({
              bookmarks,
              isAnonymous,
              isPosters,
              renderDivider: index !== tabs.length - 1,
              panel,
              showUpgradeCTA: anonymousV2,
              verticalPosition: 3,
              ...panelProps,
            });
          }
          return null;
        })}
      <Recommended {...recommendedProps} />
      {aboutProps.image && <About {...aboutProps} />}
      <Breadcrumbs data={breadcrumbsData} customStyle={SeriesDetailStyle.breadcrumbs} />
      {hasLoadedContent && <FloatingStartFreeTrial theme={SeriesDetailStyle} />}
      {hasLoadedContent && hasPromos && (
        <NationWhatIs isFreeTrialPlanActive={isFreeTrialPlanActive} promos={promos} />
      )}
      {hasLoadedContent && hasPromos && (
        <StartFreeTrial isFreeTrialPlanActive={isFreeTrialPlanActive} promos={promos} />
      )}
      <ContextPlayer
        accessToken={accessToken}
        anonymousAccount={anonymousAccount}
        onClickCTA={onClickCTA}
        onClose={onCloseModal}
        promo={promoProps}
        title={title}
        video={video}
        visibility={contextPlayerVisibility}
      />
    </>
  );
};

AnonymousView.propTypes = {
  accessToken: PropTypes.string,
  account: PropTypes.shape({}),
  anonymousAccount: PropTypes.shape({}),
  bookmarks: PropTypes.shape({}),
  breadcrumbsData: PropTypes.arrayOf(PropTypes.shape({})),
  breakpoint: PropTypes.string.isRequired,
  contextPlayerVisibility: PropTypes.bool,
  continueWatchingItems: PropTypes.arrayOf(PropTypes.shape([])),
  deviceId: PropTypes.string,
  disableScroll: PropTypes.func.isRequired,
  favoritableItems: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  favoritesManager: PropTypes.shape({}).isRequired,
  inline: PropTypes.shape({}),
  isAnonymous: PropTypes.bool.isRequired,
  isAuthenticated: PropTypes.bool,
  isPersonality: PropTypes.bool,
  latestEpisode: PropTypes.shape({}),
  onCloseModal: PropTypes.func,
  overlay: PropTypes.string,
  pageViewEvent: PropTypes.func,
  params: PropTypes.shape({}),
  preauthorizedResources: PropTypes.arrayOf(PropTypes.string),
  profileId: PropTypes.string,
  promos: PropTypes.arrayOf(PropTypes.shape({})),
  recommendationIdData: PropTypes.oneOfType([PropTypes.node, PropTypes.shape({})]),
  recommended: PropTypes.shape({}),
  renderTabs: PropTypes.func,
  seo: PropTypes.shape({}),
  seriesData: PropTypes.shape({}),
  seriesType: PropTypes.string,
  setIsBrandingClicked: PropTypes.func,
  setOverlay: PropTypes.func.isRequired,
  subTabIndex: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
  tabIndex: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
  tabs: PropTypes.arrayOf(PropTypes.shape({})),
};

export default AnonymousView;
